/**
 * ManipulativeFrame.jsx — shared shell for every teaching manipulative.
 * Cycle 55 (part 7) · Teach & Explain
 * ─────────────────────────────────────────────────────────────────
 * Wraps a work area with: title + description, collapsible settings
 * panel (children of the tool), Reset button, Projector mode (full-
 * screen, hides all chrome), Share (copy URL + QR modal). Undo/Redo
 * are opt-in — tools that want them pass `onUndo` / `onRedo` handlers.
 *
 * NO SCORE. NO GAMIFICATION. This is a demo tool — teachers project
 * it, parents show it, kids explore.
 *
 * Feb 2026 · Landscape / classroom mode: adds `?big=1` (or the toggle
 * button in the toolbar) which switches every toolbar button to a
 * 56×56 min touch target. Optimised for classroom projectors + tablet
 * kiosks where teachers use their fingers on a big display.
 */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { RotateCcw, Maximize2, Minimize2, Share2, Undo2, Redo2, Settings2, ArrowLeft, Hand, Wrench } from "lucide-react";

export default function ManipulativeFrame({
  slug, title, description, children, settingsPanel,
  onReset, onUndo, onRedo, canUndo, canRedo,
  shareUrl,  // string — the current URL with encoded state
}) {
  const [projector, setProjector] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [params, setParams] = useSearchParams();
  // Feb 2026: landscape / big-touch mode. Persists via `?big=1` so a
  // shared URL keeps the classroom-ready sizing. Also auto-enables on
  // wide+short viewports (typical projector).
  const bigParam = (params.get("big") || "").toLowerCase();
  const [big, setBig] = useState(bigParam === "1" || bigParam === "true");

  useEffect(() => {
    // Auto-detect classroom projector: landscape aspect + wide.
    if (typeof window === "undefined") return;
    const w = window.innerWidth || 0;
    const h = window.innerHeight || 1;
    if (w >= 1280 && (w / h) >= 1.55 && !bigParam) {
      setBig(true);
    }
  }, [bigParam]);

  const toggleBig = () => {
    const next = !big;
    setBig(next);
    const p = new URLSearchParams(params);
    if (next) p.set("big", "1"); else p.delete("big");
    setParams(p, { replace: true });
  };

  useEffect(() => {
    if (!showShare) return;
    // Lazy-load QR only when the modal opens.
    (async () => {
      try {
        const QR = (await import("qrcode")).default;
        const url = shareUrl || window.location.href;
        const png = await QR.toDataURL(url, { margin: 1, width: 240, color: { dark: "#0f172a", light: "#ffffff" } });
        setQrDataUrl(png);
      } catch { setQrDataUrl(""); }
    })();
  }, [showShare, shareUrl]);

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl || window.location.href);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  const framed = projector ? "fixed inset-0 z-[10000] bg-white overflow-auto" : "";
  const containerClass = projector
    ? "min-h-screen p-4 md:p-6"
    : (big ? "max-w-none w-full px-4 py-4 md:py-6" : "max-w-5xl mx-auto px-4 py-4 md:py-6");

  // Toolbar chip sizing — 56×56 tap targets in `big` mode, otherwise
  // the original compact 9-height chip.
  const chip = big
    ? "inline-flex items-center gap-2 h-14 min-w-[56px] px-5 rounded-full border-[3px] border-slate-900 font-bold text-base shadow-[0_4px_0_0_rgba(15,23,42,1)] active:translate-y-[3px] active:shadow-none transition-all"
    : "inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-slate-900 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)]";
  const chipIcon = big ? 18 : 12;

  return (
    <div className={framed} data-testid="manipulative-frame" data-big={big ? "1" : "0"}>
      <div className={containerClass}>
        {!projector && (
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <Link to="/tools" data-testid="mf-back" className={`${chip} bg-white`}>
              <ArrowLeft size={chipIcon} strokeWidth={3} /> Teach & Explain
            </Link>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              /tools/{slug}
            </span>
          </div>
        )}

        {!projector && (
          <header className="relative overflow-hidden rounded-3xl border-4 border-slate-900 bg-amber-100 p-3 sm:p-4 md:p-6 shadow-[6px_6px_0_0_rgba(15,23,42,1)]">
            {/* Paper-grain overlay so the header feels hand-drawn instead
                of a flat pastel wash. Zero network cost — inline SVG. */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-multiply"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' seed='11'/></filter><rect width='120' height='120' filter='url(%23n)' opacity='0.55'/></svg>\")",
              }}
            />
            {/* Floating accent mirrors the ToolsHub hero so the whole
                section feels visually unified. */}
            <svg aria-hidden viewBox="0 0 100 100" className="absolute -right-4 -top-4 w-20 h-20 md:w-24 md:h-24 text-fuchsia-300/50 rotate-12" fill="currentColor">
              <path d="M50 5 L60 40 L95 45 L65 60 L75 95 L50 70 L25 95 L35 60 L5 45 L40 40 Z" />
            </svg>
            <div className="relative flex items-start gap-3">
              <div className="shrink-0 inline-flex items-center justify-center h-11 w-11 md:h-16 md:w-16 rounded-2xl border-[3px] border-slate-900 bg-white text-emerald-800 shadow-[0_3px_0_0_rgba(15,23,42,1)]">
                <Wrench size={22} strokeWidth={3} className="md:hidden" />
                <Wrench size={32} strokeWidth={3} className="hidden md:block" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="inline-flex items-center gap-1 h-5 md:h-6 px-2 rounded-full border-2 border-slate-900 bg-white/90 text-[10px] font-black uppercase tracking-widest text-emerald-800 shadow-[0_2px_0_0_rgba(15,23,42,1)]">
                  Teach &amp; Explain
                </p>
                <h1
                  data-testid="mf-title"
                  className={`mt-1 font-heading font-black leading-tight text-slate-900 ${big ? "text-2xl sm:text-3xl md:text-5xl" : "text-xl sm:text-2xl md:text-3xl"}`}
                >
                  {title}
                </h1>
                {description && (
                  <p className={`mt-1 font-bold text-slate-700 max-w-2xl leading-snug ${big ? "text-sm sm:text-base md:text-lg" : "text-[13px] sm:text-sm"}`}>{description}</p>
                )}
              </div>
            </div>
          </header>
        )}

        {/* Toolbar */}
        <div className={`flex flex-wrap items-center gap-2 ${projector ? "mb-3" : "mt-3 mb-3"}`} data-testid="mf-toolbar">
          {settingsPanel && (
            <button type="button" onClick={() => setSettingsOpen((v) => !v)}
                    data-testid="mf-settings-btn"
                    className={`${chip} bg-white`}>
              <Settings2 size={chipIcon} strokeWidth={3} /> Settings
            </button>
          )}
          {onReset && (
            <button type="button" onClick={onReset} data-testid="mf-reset"
                    className={`${chip} bg-white`}>
              <RotateCcw size={chipIcon} strokeWidth={3} /> Reset
            </button>
          )}
          {onUndo && (
            <>
              <button type="button" onClick={onUndo} disabled={!canUndo} data-testid="mf-undo"
                      className={`${chip} bg-white disabled:opacity-40`}>
                <Undo2 size={chipIcon} strokeWidth={3} /> Undo
              </button>
              <button type="button" onClick={onRedo} disabled={!canRedo} data-testid="mf-redo"
                      className={`${chip} bg-white disabled:opacity-40`}>
                <Redo2 size={chipIcon} strokeWidth={3} /> Redo
              </button>
            </>
          )}
          <button type="button" onClick={() => setProjector((v) => !v)} data-testid="mf-projector"
                  className={`${chip} bg-fuchsia-200`}>
            {projector ? <Minimize2 size={chipIcon} strokeWidth={3} /> : <Maximize2 size={chipIcon} strokeWidth={3} />}
            {projector ? "Exit projector" : "Projector mode"}
          </button>
          <button type="button" onClick={toggleBig} data-testid="mf-big-toggle"
                  aria-pressed={big}
                  className={`${chip} ${big ? "bg-emerald-300" : "bg-white"}`}>
            <Hand size={chipIcon} strokeWidth={3} /> {big ? "Big touch ON" : "Big touch"}
          </button>
          <button type="button" onClick={() => setShowShare(true)} data-testid="mf-share"
                  className={`${chip} bg-yellow-300`}>
            <Share2 size={chipIcon} strokeWidth={3} /> Share (QR)
          </button>
        </div>

        {settingsPanel && settingsOpen && (
          <section className={`rounded-2xl border-[3px] border-slate-900 bg-slate-50 mb-3 ${big ? "p-5" : "p-3"}`} data-testid="mf-settings-panel">
            {settingsPanel}
          </section>
        )}

        <main data-testid="mf-work-area" className={big ? "text-lg" : ""}>{children}</main>

        {showShare && (
          <div className="fixed inset-0 z-[11000] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4"
               data-testid="mf-share-modal"
               onClick={() => setShowShare(false)}>
            <div className="bg-white rounded-2xl border-[3px] border-slate-900 p-5 max-w-md w-full shadow-[0_8px_0_0_rgba(15,23,42,1)]"
                 onClick={(e) => e.stopPropagation()}>
              <div className="flex items-baseline justify-between mb-3">
                <p className="font-heading font-black text-lg">Share this setup</p>
                <button type="button" onClick={() => setShowShare(false)} className="h-8 w-8 rounded-full border-2 border-slate-900 bg-white font-black" aria-label="Close">×</button>
              </div>
              <p className="text-xs font-bold text-slate-600 mb-3">
                Every setting is in the URL — anyone opening this link (or scanning the QR) sees exactly the same setup.
              </p>
              {qrDataUrl && (
                <div className="flex justify-center mb-3">
                  <img src={qrDataUrl} alt="QR code" className="border-2 border-slate-900 rounded-xl" data-testid="mf-share-qr" />
                </div>
              )}
              <div className="flex gap-2">
                <input readOnly value={shareUrl || window.location.href}
                       data-testid="mf-share-url"
                       className="flex-1 h-10 px-3 rounded-xl border-2 border-slate-900 bg-white font-mono text-xs" />
                <button type="button" onClick={doCopy} data-testid="mf-share-copy"
                        className="h-10 px-3 rounded-xl border-2 border-slate-900 bg-emerald-300 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)]">
                  Copy
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
